import {
  Component,
  DestroyRef,
  inject,
  Inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { catchError, from, map, mergeMap, of } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { IOption } from 'src/app/components/shared/form/components/form-select/form-select.model';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { BUTTON_CLICK } from 'src/app/components/shared/modal/exit-modal/exit-modal.constants';
import { WarningModalInput } from 'src/app/components/shared/modal/warning-modal/warning-modal.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';
import { SORTS_TYPES } from 'src/app/components/shared/table/outbound-scan-table/outbound-scan-table.constant';
import { SpecialCharacters } from 'src/app/core/enums';
import { ActiveCarryOverLoads } from 'src/app/core/models/position-operations/carry-over-loads.model';
import {
  GetGleLoadsRequest,
  GetGleLoadsResponse,
  LoadKey,
  OpenULORequest,
  OutboundScan,
} from 'src/app/core/models/position-operations/outbound-gle.model';
import {
  FlatLoadKey,
  GleLoadsResponse,
} from 'src/app/core/models/position-operations/outbound-scan-gle.model';
import { OutboundGLEService } from 'src/app/core/services/position-operations/outbound-gle/outbound-gle.service';
import { CarryOverLoadsUtils } from 'src/app/core/utils/carry-over-loads/carry-over-loads.utils';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { DataUtils } from 'src/app/core/utils/data/data.utils';
import {
  CONST,
  FORM_STEP,
  INTERNATIONAL,
  LOAD_OPTIONS,
  MOVEMENT_OPTIONS,
  SORT_LIST,
  TYPE_OPTIONS,
  ULD_FORM_Ids,
} from '../outbound-scan-gle.constant';

@Component({
  selector: 'app-add-edit-uld-modal',
  templateUrl: './add-edit-uld-modal.component.html',
  styleUrls: ['./add-edit-uld-modal.component.scss'],
  standalone: false,
})
export class AddEditUldModalComponent implements OnInit {
  readonly destroy = inject(DestroyRef);

  readonly CONST = {
    ...CONST.ULD_MODAL,
    ...CONST.TRANSFER_ULD_MODAL,
    ...CONST,
  };
  readonly SRC = IMAGE_SRC;
  readonly IDS = ULD_FORM_Ids;
  readonly FORM_STEP = FORM_STEP;
  readonly SORT_LIST = SORT_LIST;
  readonly LOAD_OPTIONS = LOAD_OPTIONS;
  readonly MOVEMENT_OPTIONS = MOVEMENT_OPTIONS;
  readonly TYPE_OPTIONS = TYPE_OPTIONS;
  readonly SPECIAL_CHARS = SpecialCharacters.ALPHABET_NUMBER_NO_SPACE;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  gleLoads: FlatLoadKey[] = [];
  loadNumbers: string[] = [];
  loadOptions!: IOption[];
  preference: string[] = [];
  activeLoads: ActiveCarryOverLoads[] = [];
  selectedLoad!: FlatLoadKey | undefined;

  uldForm!: FormGroup;
  warningDialogData: WarningModalInput = {
    header: this.CONST.MODAL.HEADER,
    body: `${this.CONST.MODAL.LINE1} <br/> ${this.CONST.MODAL.LINE2}`,
    confirmButtonText: this.CONST.MODAL.CONFIRM_BUTTON,
    rejectButtonText: this.CONST.MODAL.REJECT_BUTTON,
  };

  minDate!: Date;
  maxDate!: Date;
  currentStep = FORM_STEP.FIRST;
  isFirstStepControlDisabled = false;
  isSecondStepControlDisabled = false;

  isInternationalLoadSelected = false;
  isEditMode = false;
  gleResponse!: GleLoadsResponse;
  getGleResponse!: GetGleLoadsResponse;
  isLoadKeyDropdown = false;
  isControlDisabled = signal(false);

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      currentStep: FORM_STEP;
      isEditMode: boolean;
      index: number;
      tableData: OutboundScan[];
      formData: FormGroup['value'];
      isLoadKeyUpdate?: boolean;
      selectedLoad: ActiveCarryOverLoads;
      childLoadKey?: boolean;
      childLoadWithParent?: boolean;
      selectedParentLoad?: ActiveCarryOverLoads;
      selectedChildLoads?: ActiveCarryOverLoads[];
      isMultipleSelection?: boolean;
    },
    private readonly fb: FormBuilder,
    public dialogRef: MatDialogRef<AddEditUldModalComponent>,
    private readonly dialog: MatDialog,
    private readonly dataUtils: DataUtils,
    private readonly carryOverLoadsUtils: CarryOverLoadsUtils,
    private readonly outboundGLEService: OutboundGLEService,
    private readonly commonUtils: CommonUtils,
  ) {}

  ngOnInit(): void {
    this.setDateValidation();

    this.isEditMode = this.data?.isEditMode ?? false;

    this.isControlDisabled.set(!!this.data?.childLoadWithParent);

    this.initForm();

    this.setModalData();
  }

  /**
   * set date validation
   *
   * @memberof AddEditUldModalComponent
   */
  setDateValidation() {
    const currDate = new Date();
    this.minDate = new Date(
      currDate.getFullYear(),
      currDate.getMonth(),
      currDate.getDate() - 1,
    );
    this.maxDate = new Date(
      currDate.getFullYear(),
      currDate.getMonth(),
      currDate.getDate() + 4,
    );
  }

  /**
   * Used to define form
   *
   * @memberof AddEditUldModalComponent
   */
  initForm() {
    const sealNumber =
      this.data.isLoadKeyUpdate && !this.isControlDisabled()
        ? this.data?.selectedLoad?.sealNumber
        : null;
    this.uldForm = this.fb.group({
      [ULD_FORM_Ids.SORT_DATE]: ['', Validators.compose([Validators.required])],
      [ULD_FORM_Ids.SORT]: [null, Validators.compose([Validators.required])],
      [ULD_FORM_Ids.POSITION_BELT]: [
        null,
        Validators.compose([Validators.required]),
      ],
      [ULD_FORM_Ids.POSITION_BAY]: [
        null,
        Validators.compose([Validators.required]),
      ],
      [ULD_FORM_Ids.EQUIPMENT_NUMBER]: [null],
      [ULD_FORM_Ids.EQUIPMENT_TYPE]: [null],
      [ULD_FORM_Ids.SEAL_CONTROL]: [sealNumber],
    });
    if (this.data?.formData) {
      this.uldForm.patchValue(this.data?.formData);
    }
  }

  /**
   * used set modal form data
   *
   * @return {*}
   * @memberof AddEditUldModalComponent
   */
  setModalData() {
    if (!this.data || this.data?.currentStep === FORM_STEP.FIRST) {
      return;
    }
    this.currentStep = this.data.currentStep;
    switch (this.currentStep) {
      case FORM_STEP.SECOND:
        this.formSecondStep();
        break;
      case FORM_STEP.THIRD:
        this.isFirstStepControlDisabled = true;
        this.uldForm.addControl(
          ULD_FORM_Ids.LOAD as string,
          this.fb.control(this.data?.formData?.load, Validators.required),
        );
        this.formThirdStep();
        break;
      default:
        break;
    }
    this.uldForm.patchValue(this.data?.formData);
  }

  /**
   * used to close modal and reopen popup if REJECT button click
   *
   * @param {string} event
   * @memberof AddEditUldModalComponent
   */
  onClose(event: string) {
    this.dialogRef.close(this.uldForm.value);

    if (event === BUTTON_CLICK.REJECT) {
      return this.reOpenULDPopup();
    }
  }

  /**
   * Used reopen ULD popup on close it will set data in dataUtils newUld$ Observable
   *
   * @memberof AddEditUldModalComponent
   */
  reOpenULDPopup() {
    this.dialog
      .open(AddEditUldModalComponent, {
        width: '34.25rem',
        disableClose: true,
        autoFocus: false,
        data: {
          ...this.data,
          formData: this.uldForm.value,
          currentStep: this.currentStep,
        },
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp?.newUld) {
          this.dataUtils.newUld$.next(resp);
        } else if (resp?.editUld) {
          this.dataUtils.editUld$.next(resp);
        }
      });
  }

  /**
   * Go back to first step
   *
   * @memberof AddEditUldModalComponent
   */
  backToFirstStep() {
    this.currentStep = FORM_STEP.FIRST;
    this.uldForm.removeControl(ULD_FORM_Ids.LOAD as string);
    this.isFirstStepControlDisabled = false;
  }

  /**
   * Move to second step
   *
   * @memberof AddEditUldModalComponent
   */
  formSecondStep() {
    this.currentStep = FORM_STEP.SECOND;
    this.uldForm.addControl(
      ULD_FORM_Ids.LOAD as string,
      this.fb.control(this.data.formData?.loadKey ?? null, Validators.required),
    );
    this.getGleLoad();
    this.isFirstStepControlDisabled = true;
  }

  /**
   * Go back to second step
   *
   * @memberof AddEditUldModalComponent
   */
  backToSecondStep() {
    this.currentStep = FORM_STEP.SECOND;
    const controls = this.thirdStepControls();

    controls.forEach((control) => {
      if (!control) {
        return;
      }
      this.uldForm.removeControl(control.name as string);
    });

    this.isSecondStepControlDisabled = false;
  }

  /**
   * Move to third step
   *
   * @memberof AddEditUldModalComponent
   */
  formThirdStep() {
    this.currentStep = FORM_STEP.THIRD;
    const controls = this.thirdStepControls();
    controls.forEach((control) => {
      if (!control) {
        return;
      }
      this.uldForm.addControl(
        control.name as string,
        this.fb.control(
          control.value,
          control.required ? Validators.required : null,
        ),
      );

      if (!control.required) {
        this.uldForm.get(control.name)?.disable();
      }
    });

    this.isSecondStepControlDisabled = true;
  }

  getGleLoad() {
    const request: GetGleLoadsRequest = {
      sortCode: this.getSortCode(),
      belt: this.uldForm.get('positionBelt')?.value,
      bay: this.uldForm.get('positionBay')?.value,
      sortDate: this.carryOverLoadsUtils.formatDate(
        this.uldForm.get('sortDate')?.value,
      ),
      newUld: this.data.isLoadKeyUpdate ? false : true,
    };
    const sealNumber = this.data?.selectedLoad?.sealNumber;

    const updatedRequest = this.data.isLoadKeyUpdate
      ? {
          ...request,
          uldNumber: this.data.selectedLoad.uldNumber,
          uldType: this.data.selectedLoad.uldType,
          originSort: this.data.selectedLoad.origSort,
          loadKey: this.data.selectedLoad.loadKey,
          loadCreateDate: this.carryOverLoadsUtils.formatDate(
            this.data.selectedLoad.loadCreateDate,
          ),
          ...(sealNumber &&
            this.data.isLoadKeyUpdate && {
              movement: this.data.selectedLoad.movement,
              sealNumber: this.data.selectedLoad.sealNumber,
            }),
        }
      : request;

    this.outboundGLEService
      .getGleLoads(updatedRequest)
      .pipe(
        map((res) => {
          if (res?.response) {
            this.getGleResponse = res;
            res.response.loadKeys.forEach((load) => {
              this.gleLoads.push(load);
              this.loadNumbers.push(load?.name);
            });
            const carryOverLoads = this.gleLoads.filter(
              (item) => item.carryover,
            );
            this.isLoadKeyDropdown = carryOverLoads.length > 1;
            this.preference = carryOverLoads.map((load) => load.preference);

            // Carry over laods
            if (this.data?.isLoadKeyUpdate && this.isLoadKeyDropdown) {
              this.uldForm.get(ULD_FORM_Ids.LOAD)?.setValue(null);
              this.loadOptions = this.convertToOptions(carryOverLoads);
              this.uldForm
                .get(ULD_FORM_Ids.MOVEMENT)
                ?.setValue(this.gleLoads[0].mvmtNum);
            }
            // Non carryover loads
            else {
              this.loadOptions = this.convertToOptions(this.gleLoads);
            }
          } else {
            this.dialogRef.close();
          }
        }),
      )
      .subscribe();
  }
  getSortCode(): string {
    if (this.uldForm.get('sort')?.value.includes('-')) {
      return this.getSort(this.uldForm.get('sort')?.value);
    } else {
      return this.uldForm.get('sort')?.value;
    }
  }

  /**
   * Returns Sort for given accronyms
   *
   * @param {OutboundScan} outboundScan
   * @return {*}  {string}
   * @memberof OutboundScanTableComponent
   */
  getSort(sortCode: string): string {
    switch (sortCode) {
      case SORTS_TYPES.SUNRISE:
        return 'S';
      case SORTS_TYPES.MORNING:
        return 'M';
      case SORTS_TYPES.DAY:
        return 'D';
      case SORTS_TYPES.TWILIGHT:
        return 'T';
      case SORTS_TYPES.NIGHT:
        return 'N';
      case SORTS_TYPES.PRELOAD:
        return 'P';
      case SORTS_TYPES.LOCAL:
        return 'L';
      case SORTS_TYPES.AFTERNOON:
        return 'A';
      default:
        return '';
    }
  }

  /**
   * Creates a GLE Loads Request object based on the current form values and active loads.
   *
   * @returns {GleLoadsRequest} The constructed GLE Loads Request object.
   */
  createOpenNewGleRequest(): OpenULORequest {
    const formLoad = this.uldForm.get('load')?.value;
    const load = this.getSelectedLoadDetails(formLoad);

    if (!load) {
      throw new Error('Load not found');
    }

    const loadKey: LoadKey = {
      discriminator: this.selectedLoad?.discriminator,
      origin: {
        country: load.origCountry,
        slic: load.origSlic,
        portCode: load.origPortcode,
        sortCode: load.origSortcode,
        qualifier: load.origSortQual,
      },
      destination: {
        country: load.destCountry,
        slic: load.destSlic,
        portCode: load.destPortcode,
        sortCode: load.destSortcode,
        qualifier: load.destSortQual,
      },
      processing: {
        country: load.procCountry,
        slic: load.procSlic,
        portCode: load.procPortcode,
        sortCode: load.procSortcode,
        qualifier: load.procSortQual,
      },
      uld: {
        number: this.uldForm.get('equipmentNumber')?.value,
        seal: this.uldForm.get('sealControl')?.value,
        type: this.uldForm.get('equipmentType')?.value,
      },
      packageLocationRole: load.pkglocrole,
      service: load.srvLevel,
      disposition: load.disposition,
      scanType: load.scanType,
      loadChart: load.loadChart,
      movement: {
        mvmtNum: load.mvmtNum,
        mvmtType: load.mvmtType,
      },
      usps: load.usps,
      carryOver: load.carryover,
      bulk: load.bulk,
      loadType: load.loadType,
      flowType: load.flowType,
      position: {
        belt: this.uldForm.get('positionBelt')?.value,
        bay: this.uldForm.get('positionBay')?.value,
      },
      loadName: load.name,
    };

    const request: OpenULORequest = {
      sortDate: this.carryOverLoadsUtils.formatDate(
        this.uldForm.get('sortDate')?.value,
      ),
      loadKey: loadKey,
    };

    return request;
  }

  /**
   * Used to create third step control based on selected load
   *
   * @return {*}
   * @memberof AddEditUldModalComponent
   */
  thirdStepControls() {
    const loadValue = this.uldForm.get(ULD_FORM_Ids.LOAD)?.value;
    this.selectedLoad = this.getSelectedLoadDetails(loadValue);
    this.isInternationalLoadSelected =
      this.selectedLoad?.discriminator === INTERNATIONAL ||
      !!(this.data.isLoadKeyUpdate && this.data.selectedLoad.sealNumber);
    const sealNumber =
      this.data.isLoadKeyUpdate && !this.isControlDisabled()
        ? this.data?.selectedLoad?.sealNumber
        : null;
    const sealControl = this.isInternationalLoadSelected
      ? {
          name: ULD_FORM_Ids.SEAL_CONTROL,
          value: sealNumber,
          required: true,
        }
      : null;

    const controls = [
      ...(this.isInternationalLoadSelected && this.data?.isLoadKeyUpdate
        ? [
            {
              name: ULD_FORM_Ids.MOVEMENT,
              value: this.selectedLoad?.mvmtNum || this.gleLoads[0].mvmtNum,
              required: true,
            },
          ]
        : []),
      {
        name: ULD_FORM_Ids.EQUIPMENT_NUMBER,
        value: null,
        required: true,
      },
      {
        name: ULD_FORM_Ids.EQUIPMENT_TYPE,
        value: null,
        required: true,
      },
      sealControl,
    ];
    return controls;
  }

  getSelectedLoadDetails(selectedValue: string): FlatLoadKey | undefined {
    const [name, mvmtNum] = selectedValue.split('|');
    if (mvmtNum) {
      return this.gleLoads.find(
        (load) => load.name === name && load.mvmtNum === mvmtNum, // International
      );
    } else {
      // Setting 1st element as the backup option for cases where loadValue does not match
      return (
        this.gleLoads.find((dat) => dat.name === name) ?? this.gleLoads?.[0]
      );
    }
  }

  /**
   * used to submit form value close modal
   *
   * @memberof AddEditUldModalComponent
   */
  submit() {
    if (this.data?.isLoadKeyUpdate) {
      const sealNumber = this.data?.selectedLoad?.sealNumber;
      if (this.data?.isMultipleSelection) {
        const items: any[] = [
          ...(this.data?.selectedParentLoad
            ? [this.data?.selectedParentLoad]
            : []),
          ...(this.data?.selectedChildLoads || []),
        ];

        from(items)
          .pipe(
            mergeMap((item) => {
              const sealNumber = item.sealNumber;

              const updatedData = {
                ...item,
                loadKeys: this.gleLoads,
                bay: this.uldForm.get(ULD_FORM_Ids.POSITION_BAY)?.value,
                belt: this.uldForm.get(ULD_FORM_Ids.POSITION_BELT)?.value,
                sortCode: this.uldForm.get(ULD_FORM_Ids.SORT)?.value,
                ...this.uldForm.value,
                sortDate: this.carryOverLoadsUtils.formatDate(
                  this.uldForm.get('sortDate')?.value,
                ),
                uloId: item.id,
                ...(sealNumber && {
                  movement:
                    this.uldForm.get(ULD_FORM_Ids.MOVEMENT)?.value ||
                    item.movement,
                  sealNumber:
                    this.uldForm.get(ULD_FORM_Ids.SEAL_CONTROL)?.value ||
                    item.sealNumber,
                }),
                ...(this.data?.childLoadWithParent && {
                  equipmentNumber:
                    this.uldForm.get(ULD_FORM_Ids.EQUIPMENT_NUMBER)?.value ||
                    item.uldNumber,
                  equipmentType:
                    this.uldForm.get(ULD_FORM_Ids.EQUIPMENT_TYPE)?.value ||
                    item.uldType,
                }),
              };
              return this.carryOverLoadsUtils
                .updateGleLoad(updatedData, item, this.selectedLoad)
                .pipe(
                  catchError((error) => {
                    return of({ code: 500, error });
                  }),
                );
            }),
          )
          .subscribe({
            next: (res) => {
              if (res?.code === 200) {
                this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
              }
            },
            complete: () => {
              const formValue = this.uldForm.value;
              this.dialogRef.close({
                updateUld: formValue,
              });
            },
          });
      } else {
        const updatedData = {
          ...this.data.selectedLoad,
          loadKeys: this.gleLoads,
          bay: this.uldForm.get(ULD_FORM_Ids.POSITION_BAY)?.value,
          belt: this.uldForm.get(ULD_FORM_Ids.POSITION_BELT)?.value,
          sortCode: this.uldForm.get(ULD_FORM_Ids.SORT)?.value,
          ...this.uldForm.value,
          sortDate: this.carryOverLoadsUtils.formatDate(
            this.uldForm.get('sortDate')?.value,
          ),
          uloId: this.getGleResponse.response.uloId,
          ...(sealNumber && {
            movement:
              this.uldForm.get(ULD_FORM_Ids.MOVEMENT)?.value ||
              this.data.selectedLoad.movement,
            sealNumber:
              this.uldForm.get(ULD_FORM_Ids.SEAL_CONTROL)?.value ||
              this.data.selectedLoad.sealNumber,
          }),
          ...(this.data?.childLoadWithParent && {
            equipmentNumber: this.data?.selectedLoad.uldNumber,
            equipmentType: this.data?.selectedLoad.uldType,
          }),
        };

        this.carryOverLoadsUtils
          .updateGleLoad(updatedData, this.data.selectedLoad, this.selectedLoad)
          .subscribe((response) => {
            if (response.code === 200) {
              this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
              const formValue = this.uldForm.value;
              this.dialogRef.close({
                updateUld: formValue,
              });
            }
          });
      }
      return;
    }

    this.outboundGLEService
      .openNewUlo(this.createOpenNewGleRequest())
      .pipe(
        catchError((errorResponse) => {
          const errorMessage =
            errorResponse?.error?.errors?.[0]?.message ||
            'Unexpected error occurred';
          this.commonUtils.updateBannerType400Error(errorMessage);
          this.dialogRef.close();

          return of(null);
        }),
      )
      .subscribe((response) => {
        if (response?.code === 200) {
          this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
          const formValue = this.uldForm.value;
          this.dialogRef.close({
            newUldRow: formValue,
            newUldRequest: this.createOpenNewGleRequest(),
          });
        } else {
          this.dialogRef.close();
        }
      });
  }

  /**
   * used to show cancel warning modal
   *
   * @memberof AddEditUldModalComponent
   */
  cancel() {
    this.commonUtils
      .openWarningModal({
        header: this.CONST.CANCEL_MODAL.HEADER,
        body: this.CONST.CANCEL_MODAL.BODY,
        confirmButtonText: this.CONST.CANCEL_MODAL.CONFIRM_BUTTON,
        rejectButtonText: this.CONST.CANCEL_MODAL.REJECT_BUTTON,
        extraClass: 'large-confirm-btn',
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp.button === BUTTON_CLICK.ACCEPT) {
          this.dialogRef.close();
        }
      });
  }

  /**
   * used to save form value close modal
   *
   * @memberof AddEditUldModalComponent
   */
  save() {
    const formValue = this.uldForm.value;
    this.outboundGLEService
      .openNewUlo(this.createOpenNewGleRequest())
      .subscribe();
    this.dialogRef.close({
      editUld: formValue,
      index: this.data?.index,
      tableData: this.data?.tableData,
    });
  }

  convertToOptions(loads: FlatLoadKey[]): IOption[] {
    return loads.map((load) => ({
      label: load.mvmtNum
        ? `${load.selectedLoadName} (${load.mvmtNum})`
        : `${load.selectedLoadName}`,
      value: load.mvmtNum ? `${load.name}|${load.mvmtNum}` : `${load.name}`,
      tooltip: load.preference,
    }));
  }
}
